import { useEffect, useMemo } from 'preact/hooks'
import { type WebViewDto } from '@/api/webs'
import { useWebLoader } from '@/hooks/useWebLoader'
import { useWebTabRouter, type WebTabKey } from '@/hooks/useWebTabRouter'
import { usePageHeader } from '@/hooks/usePageHeader'
import { routes } from '@/utils/routes'
import { WebDetailHeader } from './WebDetailHeader'
import { WebDetailsTab } from './tabs/WebDetailsTab'
import { WebQuizTab } from './tabs/WebQuizTab'
import { WebActivationsTab } from './tabs/WebActivationsTab'
import { WebEmailBlastsTab } from './tabs/WebEmailBlastsTab'
import { MeetingsTab } from '@/components/common/MeetingsTab'
import { WebLeadsTab } from './tabs/WebLeadsTab'
import { WebSettingsTab } from './tabs/WebSettingsTab'
import { WebCalendarTab } from './tabs/WebCalendarTab'
import { WebAssetsTab } from './tabs/WebAssetsTab'
import { LandingPageTab } from './tabs/LandingPageTab'
import { loadEffectiveSettings, clearEffectiveSettings } from '@/state/workflowSettings'
import { useBigMarkerIntegration } from '@/hooks/useBigMarkerIntegration'
import { useSpeakerImport } from '@/hooks/useSpeakerImport'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'

interface Props { clientCompanyId: string; workspaceId: string; campaignId: string; webId: string }

export function WebDetail({ clientCompanyId, workspaceId, campaignId, webId }: Props) {
    const { setHeader } = usePageHeader()
    const { web, setWeb, loading, error, reload } = useWebLoader(workspaceId, webId)
    const { activeTab, routeToTab } = useWebTabRouter({ clientCompanyId, workspaceId, campaignId, webId })
    const { hasBigMarker, integrationError } = useBigMarkerIntegration(workspaceId)
    const hasHealthyBigMarker = hasBigMarker && !integrationError
    const { canEdit } = useWorkspaceCapabilities()
    const { importingSpeakers } = useSpeakerImport({ web, hasBigMarker: hasHealthyBigMarker, canEdit, setWeb })

    // Pre-load effective workflow settings for this web (signals store)
    useEffect(() => {
        loadEffectiveSettings(clientCompanyId, workspaceId, campaignId, webId)
        return () => { clearEffectiveSettings() }
    }, [clientCompanyId, workspaceId, campaignId, webId])

    // Breadcrumb header
    useEffect(() => {
        const scoped = routes.scoped(clientCompanyId, workspaceId)
        setHeader({ items: [
            { text: 'Campaigns', href: scoped.campaignsIndex() },
            { text: 'Campaign', href: scoped.campaign(campaignId) },
            { text: 'Web' }
        ] })
    }, [clientCompanyId, workspaceId, campaignId])

    // Type flags
    const isOnlineEvent = useMemo(() => (web?.Type || '').toLowerCase().includes('online') || !!web?.OnlineEvent, [web])
    const isDownload = useMemo(() => (web?.Type || '').toLowerCase().includes('download') || !!web?.Download, [web])
    const isMeeting = web?.Type === 'Meeting'
    const isDemo = web?.Type === 'Demo'

    if (loading && !web) return (<div className="p-6 flex items-center gap-3 text-slate-600 dark:text-slate-300"><div className="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />Loading web...</div>)
    if (error && !web) return <div className="p-6 text-red-600 dark:text-red-400">{error}</div>
    if (!web) return null

    const statusTone = web.Status?.toLowerCase() === 'active'
        ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
        : 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200'

    const displayTitle = (() => {
        if (isOnlineEvent) return web.OnlineEvent?.Title || web.Name
        if (isDownload) return web.Download?.Title || web.Name
        if (isMeeting) return web.Meeting?.Title || web.Name
        if (isDemo) return web.Demo?.Title || web.Name
        return web.Name
    })()

    const tabDefinitions: Array<{ key: WebTabKey; label: string }> = [
        { key: 'details', label: 'Details' },
        ...(isDownload ? [{ key: 'quiz' as WebTabKey, label: 'Quiz' }] : []),
        { key: 'activations', label: 'Activations' },
        { key: 'email-sends', label: 'Broadcasts' },
        { key: 'meetings', label: 'Meetings' },
        { key: 'calendar', label: 'Calendar' },
        { key: 'leads', label: 'Leads' },
        { key: 'media', label: 'Assets' },
        { key: 'landing-page' as WebTabKey, label: 'Landing Page' },
        { key: 'settings', label: 'Settings' },
    ]

    return (
        <div className="p-6 space-y-6">
            <WebDetailHeader
                web={web} setWeb={setWeb} displayTitle={displayTitle} isOnlineEvent={isOnlineEvent}
                statusTone={statusTone} workspaceId={workspaceId} campaignId={campaignId} webId={webId}
                hasBigMarker={hasBigMarker} integrationError={integrationError} importingSpeakers={importingSpeakers} routeToTab={routeToTab} activeTab={activeTab}
            />
            {/* Tabs navigation */}
            <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 dark:border-slate-700">
                {tabDefinitions.map(t => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => routeToTab(t.key)}
                        className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors relative
                            ${activeTab === t.key ? 'text-primary-700 dark:text-primary-400' : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'}
                        `}
                    >
                        {t.label}
                        <span className={`absolute left-0 right-0 -bottom-px h-0.5 rounded-full transition-all ${activeTab === t.key ? 'bg-primary-600 dark:bg-primary-500' : 'bg-transparent'}`} />
                    </button>
                ))}
            </div>
            {/* Active tab content */}
            <div>
                {activeTab === 'details' && <WebDetailsTab web={web} onPatchWeb={(patcher: (prev: WebViewDto) => WebViewDto) => setWeb((prev: WebViewDto | null) => (prev ? patcher(prev) : prev))} importingSpeakers={importingSpeakers} routeToTab={routeToTab} />}
                {activeTab === 'quiz' && isDownload && <WebQuizTab web={web} onPatchWeb={(patcher: (prev: WebViewDto) => WebViewDto) => setWeb((prev: WebViewDto | null) => (prev ? patcher(prev) : prev))} />}
                {activeTab === 'meetings' && <MeetingsTab web={web} workspaceId={workspaceId} campaignId={campaignId} />}
                {activeTab === 'calendar' && <WebCalendarTab web={web} clientCompanyId={clientCompanyId} />}
                {activeTab === 'activations' && <WebActivationsTab clientCompanyId={clientCompanyId} web={web} workspaceId={workspaceId} campaignId={campaignId} />}
                {activeTab === 'email-sends' && <WebEmailBlastsTab web={web} clientCompanyId={clientCompanyId} workspaceId={workspaceId} campaignId={campaignId} />}
                {activeTab === 'leads' && <WebLeadsTab web={web} clientCompanyId={clientCompanyId} workspaceId={workspaceId} campaignId={campaignId} />}
                {activeTab === 'media' && <WebAssetsTab web={web} clientCompanyId={clientCompanyId} workspaceId={workspaceId} campaignId={campaignId} onPatchWeb={(patcher: (prev: WebViewDto) => WebViewDto) => setWeb((prev: WebViewDto | null) => (prev ? patcher(prev) : prev))} routeToTab={routeToTab} onBuilderClosed={reload} />}
                {activeTab === 'landing-page' && <LandingPageTab web={web} clientCompanyId={clientCompanyId} workspaceId={workspaceId} campaignId={campaignId} routeToTab={routeToTab} />}
                {activeTab === 'settings' && <WebSettingsTab web={web} onPatchWeb={(patcher: (prev: WebViewDto) => WebViewDto) => setWeb((prev: WebViewDto | null) => (prev ? patcher(prev) : prev))} clientCompanyId={clientCompanyId} workspaceId={workspaceId} campaignId={campaignId} />}
            </div>
        </div>
    )
}
